import { useLayoutEffect, useRef, type InputHTMLAttributes } from 'react';

/** «1234567.5» → «1.234.567,5»: thousands with dots and the decimal comma, as money is written in Colombia. */
export function groupDigits(plain: string): string {
  if (plain === '') return '';
  const negative = plain.startsWith('-');
  const [int = '', dec] = plain.replace('-', '').split('.');
  const grouped = int.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${negative ? '-' : ''}${grouped}${dec !== undefined ? `,${dec}` : ''}`;
}

/** What the person typed («1.234.567,5», «1234567») → the plain number the forms keep («1234567.5»). */
export function plainDigits(typed: string, decimals: number, negative = false): string {
  const minus = negative && typed.trim().startsWith('-') ? '-' : '';
  let s = typed.replace(/[^\d,]/g, '');
  const comma = s.indexOf(',');
  if (comma >= 0) {
    const dec = s.slice(comma + 1).replace(/,/g, '').slice(0, decimals);
    s = decimals > 0 ? `${s.slice(0, comma)}.${dec}` : s.slice(0, comma);
  }
  return minus + s.replace(/^0+(?=\d)/, '');
}

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'inputMode'> & {
  /** The amount as a plain number string («12000», «12000.5», or «» when empty). */
  value: string | number;
  onChange: (value: string) => void;
  /** Decimals the person may type after a comma (0 for whole pesos). */
  decimals?: number;
  /** A leading «-» is kept (a discount). */
  negative?: boolean;
};

/**
 * A money amount typed with its thousands dots shown as you write («1.250.000»), while the form
 * keeps the plain number. The caret stays after the same digit when the dots move.
 */
export function MoneyInput({ value, onChange, decimals = 0, negative = false, className = 'input', ...rest }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  // digits left of the caret after the last edit: the caret goes back after the same digit once the dots move
  const caret = useRef<number | null>(null);
  const shown = groupDigits(String(value ?? ''));
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || caret.current === null || document.activeElement !== el) return;
    let digits = caret.current;
    let pos = 0;
    while (pos < shown.length && digits > 0) {
      if (/[\d,]/.test(shown[pos]!)) digits--;
      pos++;
    }
    el.setSelectionRange(pos, pos);
    caret.current = null;
  }, [shown]);
  return (
    <input
      {...rest}
      ref={ref}
      className={className}
      type="text"
      // phone keypads have no minus sign: a discount gets the full keyboard
      inputMode={negative ? 'text' : decimals > 0 ? 'decimal' : 'numeric'}
      autoComplete="off"
      value={shown}
      onChange={(e) => {
        const at = e.target.selectionStart ?? e.target.value.length;
        caret.current = e.target.value.slice(0, at).replace(/[^\d,]/g, '').length;
        onChange(plainDigits(e.target.value, decimals, negative));
      }}
    />
  );
}
